/* ═══════════════════════════════════════════════════════════════
   Level Properties panel — extracted from the classic hand-built forma
   canvas (CanvasComponents.tsx) when that canvas retired (contract §3
   R6, phase C: parity proven). Real, active consumer: the Designer/
   Studio's shared Levels palette (designer/DesignPalettes.tsx's
   LevelPalette, used by both the Designer's project-forma editing and
   the Master Forma Studio) — not classic-only, so it survives the
   retirement as its own small module.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Check } from 'lucide-react';
import { CSI_NUMBER_FORMATS } from '../constants';
import { effectiveCase } from '../utils';
import { Hint } from './ui/tooltip';
import { FORMA_TIPS } from '../formaTooltips';

export function LevelProps({
  level,
  levelIdx,
  onUpdate,
  numFormats,
  onNumFmtChange,
}: {
  level: any;
  levelIdx: number;
  onUpdate: (l: any) => void;
  numFormats: string[];
  onNumFmtChange: (idx: number, fmt: string) => void;
}) {
  const tog = (k: string) => onUpdate({ ...level, [k]: !level[k] });
  const setCase = (c: 'upper' | 'title' | 'sentence') => {
    const next = effectiveCase(level) === c ? undefined : c;
    onUpdate({ ...level, textCase: next, allCaps: next === 'upper' });
  };
  const charBtns = [
    { k: 'bold', l: 'B', s: { fontWeight: 700 }, tip: FORMA_TIPS.levelBold, aria: 'Bold' },
    {
      k: 'italic',
      l: 'I',
      s: { fontStyle: 'italic' },
      tip: FORMA_TIPS.levelItalic,
      aria: 'Italic',
    },
    {
      k: 'underline',
      l: 'U',
      s: { textDecoration: 'underline' },
      tip: FORMA_TIPS.levelUnderline,
      aria: 'Underline',
    },
  ];
  const caseBtns: Array<{ c: 'upper' | 'title' | 'sentence'; l: string; t: string }> = [
    { c: 'upper', l: 'AA', t: FORMA_TIPS.levelCaseUpper },
    { c: 'title', l: 'Aa', t: FORMA_TIPS.levelCaseTitle },
    { c: 'sentence', l: 'ab', t: FORMA_TIPS.levelCaseSentence },
  ];
  const pagBtns = [
    { k: 'keepWithNext', l: 'Keep w/ next', tip: FORMA_TIPS.levelKeepWithNext },
    { k: 'keepLinesTogether', l: 'Keep together', tip: FORMA_TIPS.levelKeepTogether },
    { k: 'noSpaceSameStyle', l: 'No space same', tip: FORMA_TIPS.levelNoSpaceSame },
  ];
  const LS: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--tm)',
    textTransform: 'uppercase',
    letterSpacing: '.05em',
  };
  const IS: React.CSSProperties = {
    width: 56,
    padding: '4px 6px',
    borderRadius: 4,
    border: '1px solid var(--bd)',
    background: 'var(--ib)',
    color: 'var(--tx)',
    fontSize: 11,
    fontFamily: "'JetBrains Mono'",
  };

  const fmtInfo = (CSI_NUMBER_FORMATS as any)[levelIdx];
  const hasOptions = fmtInfo && fmtInfo.options.length > 1;
  const currentFmt = numFormats ? numFormats[levelIdx] : fmtInfo?.options[0]?.id;

  // sf-palette-flow: when this renders inside a wide/short dock panel its
  // sections tile into columns like every other palette (FormaDock.css).
  // Inert in the right-click popover, which is never .forma-panel-horizontal.
  return (
    <div
      className="sf-palette-flow"
      style={{
        padding: '12px 16px',
        background: 'var(--sf)',
        fontSize: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={LS}>Character</span>
        <div style={{ display: 'flex', gap: 3 }}>
          {charBtns.map((b) => (
            <Hint key={b.k} text={b.tip}>
              <button
                aria-label={b.aria}
                aria-pressed={!!level[b.k]}
                onClick={() => tog(b.k)}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 4,
                  border: `1px solid ${level[b.k] ? 'var(--ac)' : 'var(--bd)'}`,
                  background: level[b.k] ? 'var(--ac)' : 'transparent',
                  color: level[b.k] ? 'var(--color-text-inverse)' : 'var(--tx)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...b.s,
                  transition: 'all .15s',
                }}
              >
                {b.l}
              </button>
            </Hint>
          ))}
          <div style={{ width: 1, background: 'var(--bd)', margin: '2px 1px' }} />
          {caseBtns.map((b) => {
            const on = effectiveCase(level) === b.c;
            return (
              <Hint key={b.c} text={b.t}>
                <button
                  aria-label={b.t}
                  aria-pressed={on}
                  onClick={() => setCase(b.c)}
                  style={{
                    width: 28,
                    height: 26,
                    borderRadius: 4,
                    border: `1px solid ${on ? 'var(--ac)' : 'var(--bd)'}`,
                    background: on ? 'var(--ac)' : 'transparent',
                    color: on ? 'var(--color-text-inverse)' : 'var(--tx)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    transition: 'all .15s',
                  }}
                >
                  {b.l}
                </button>
              </Hint>
            );
          })}
          <Hint text={FORMA_TIPS.levelColor}>
            <input
              type="color"
              aria-label="Text color"
              value={`#${level.fontColor || '000000'}`}
              onChange={(e) => onUpdate({ ...level, fontColor: e.target.value.replace('#', '') })}
              style={{
                width: 26,
                height: 26,
                border: '1px solid var(--bd)',
                borderRadius: 4,
                cursor: 'pointer',
                padding: 0,
                marginLeft: 4,
              }}
            />
          </Hint>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={LS}>Alignment</span>
        <div style={{ display: 'flex', gap: 3 }}>
          {(
            [
              {
                v: 'left',
                t: FORMA_TIPS.levelAlignLeft,
                a: 'Align left',
                d: 'M2 3h10M2 6h7M2 9h10M2 12h7',
              },
              {
                v: 'center',
                t: FORMA_TIPS.levelAlignCenter,
                a: 'Center',
                d: 'M2 3h10M3.5 6h7M2 9h10M3.5 12h7',
              },
              {
                v: 'right',
                t: FORMA_TIPS.levelAlignRight,
                a: 'Align right',
                d: 'M2 3h10M5 6h7M2 9h10M5 12h7',
              },
              {
                v: 'justify',
                t: FORMA_TIPS.levelAlignJustify,
                a: 'Justify',
                d: 'M2 3h10M2 6h10M2 9h10M2 12h10',
              },
            ] as Array<{ v: string; t: string; a: string; d: string }>
          ).map((b) => {
            const on = (level.justify || 'left') === b.v;
            return (
              <Hint key={b.v} text={b.t}>
                <button
                  aria-label={b.a}
                  aria-pressed={on}
                  onClick={() => onUpdate({ ...level, justify: b.v === 'left' ? undefined : b.v })}
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 4,
                    border: `1px solid ${on ? 'var(--ac)' : 'var(--bd)'}`,
                    background: on ? 'var(--ac)' : 'transparent',
                    color: on ? 'var(--color-text-inverse)' : 'var(--tx)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all .15s',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                    <path d={b.d} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  </svg>
                </button>
              </Hint>
            );
          })}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={LS}>Indent (in)</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <div>
            <Hint text={FORMA_TIPS.levelIndent}>
              <span tabIndex={0} style={{ fontSize: 11, color: 'var(--tm)' }}>
                Left
              </span>
            </Hint>
            <br />
            <input
              type="number"
              aria-label="Left indent, inches"
              step="0.25"
              min="0"
              max="5"
              value={level.indent}
              onChange={(e) =>
                onUpdate({ ...level, indent: Math.max(0, Number(e.target.value) || 0) })
              }
              style={IS}
            />
          </div>
          <div>
            <Hint text={FORMA_TIPS.levelHanging}>
              <span tabIndex={0} style={{ fontSize: 11, color: 'var(--tm)' }}>
                Hanging
              </span>
            </Hint>
            <br />
            <input
              type="number"
              aria-label="Hanging indent, inches"
              step="0.25"
              min={-(level.indent || 0)}
              max="3"
              value={level.hangingIndent}
              onChange={(e) =>
                onUpdate({
                  ...level,
                  hangingIndent: Math.max(-(level.indent || 0), Number(e.target.value) || 0),
                })
              }
              style={IS}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={LS}>Spacing (pt)</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <div>
            <Hint text={FORMA_TIPS.levelSpaceBefore}>
              <span tabIndex={0} style={{ fontSize: 11, color: 'var(--tm)' }}>
                Before
              </span>
            </Hint>
            <br />
            <input
              type="number"
              aria-label="Space before, points"
              step="1"
              min="0"
              max="72"
              value={level.spaceBefore}
              onChange={(e) =>
                onUpdate({ ...level, spaceBefore: Math.max(0, Number(e.target.value) || 0) })
              }
              style={{ ...IS, width: 48 }}
            />
          </div>
          <div>
            <Hint text={FORMA_TIPS.levelSpaceAfter}>
              <span tabIndex={0} style={{ fontSize: 11, color: 'var(--tm)' }}>
                After
              </span>
            </Hint>
            <br />
            <input
              type="number"
              aria-label="Space after, points"
              step="1"
              min="0"
              max="72"
              value={level.spaceAfter}
              onChange={(e) =>
                onUpdate({ ...level, spaceAfter: Math.max(0, Number(e.target.value) || 0) })
              }
              style={{ ...IS, width: 48 }}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={LS}>Pagination</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {pagBtns.map((c) => (
            <Hint key={c.k} text={c.tip}>
              <label
                tabIndex={0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'pointer',
                  fontSize: 11,
                }}
              >
                <div
                  role="checkbox"
                  aria-checked={!!level[c.k]}
                  aria-label={c.l}
                  onClick={() => tog(c.k)}
                  style={{
                    width: 15,
                    height: 15,
                    borderRadius: 3,
                    border: `1.5px solid ${level[c.k] ? 'var(--ac)' : 'var(--bd)'}`,
                    background: level[c.k] ? 'var(--ac)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-inverse)',
                    fontSize: 11,
                    cursor: 'pointer',
                    transition: 'all .15s',
                    flexShrink: 0,
                  }}
                >
                  {level[c.k] ? <Check size={11} strokeWidth={3} aria-hidden /> : null}
                </div>
                {c.l}
              </label>
            </Hint>
          ))}
        </div>
      </div>
      {hasOptions && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Hint text={FORMA_TIPS.levelNumbering}>
            <span tabIndex={0} style={LS}>
              Numbering
            </span>
          </Hint>
          <div style={{ display: 'flex', gap: 4 }}>
            {fmtInfo.options.map((o: any) => (
              <button
                key={o.id}
                onClick={() => onNumFmtChange(levelIdx, o.id)}
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  border: `1px solid ${currentFmt === o.id ? 'var(--ac)' : 'var(--bd)'}`,
                  background: currentFmt === o.id ? 'rgba(var(--acr),0.12)' : 'transparent',
                  color: currentFmt === o.id ? 'var(--ac)' : 'var(--tm)',
                  fontWeight: currentFmt === o.id ? 700 : 400,
                  fontSize: 11,
                  cursor: 'pointer',
                  fontFamily: "'JetBrains Mono'",
                  transition: 'all .15s',
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
